import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { isMasked, toAmount, safeCalc } from "../../utils/maskedValue";

export function formatTrackerCurrency(value) {
  if (isMasked(value)) return value.trim();
  return `$${toAmount(value).toLocaleString("en-IN")}`;
}

export const invoiceStatusConfig = {
  Approved: {
    color: "success",
    icon: <CheckCircleOutlined />,
    dot: "#52c41a",
  },
  "In Review": {
    color: "processing",
    icon: <ClockCircleOutlined />,
    dot: "#1677ff",
  },
  Rejected: {
    color: "error",
    icon: <CloseCircleOutlined />,
    dot: "#ff0000",
  },
  Pending: {
    color: "warning",
    icon: <ExclamationCircleOutlined />,
    dot: "#FABB02",
  },
  "Not Submitted": {
    color: "default",
    icon: <ClockCircleOutlined />,
    dot: "#bfbfbf",
  },
};

const buildMilestoneRowKey = (row) =>
  [
    row.projectId || "",
    row.sowId || "",
    row.milestoneId || "",
    row.milestoneName || "",
    row.startDate || "",
    row.endDate || "",
    String(row.amount ?? 0),
    row.status || "",
  ].join("|");

/** Removes milestone rows that resolve to the same logical record (same project/sow/milestone/amount/status). */
export function dedupeMilestones(rows) {
  const seen = new Set();
  return rows.filter((row) => {
    const key = buildMilestoneRowKey(row);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function getCurrencySymbol(currencyType){
  const normalizedCurrency = String(currencyType || "").trim().toUpperCase();
  if (normalizedCurrency === "USD") return "$";
  if (normalizedCurrency === "INR") return "₹";
  return "";
}

export function formatAmountWithCurrencyType(amount, currencyType) {
  const symbol = getCurrencySymbol(currencyType);
  if (isMasked(amount)) return amount.trim();
  const formatted = toAmount(amount).toLocaleString("en-IN");
  return symbol ? `${symbol}${formatted}` : formatted;
}

/** Normalizes the raw tracker summary payload (milestone or invoice endpoints) into a typed summary. */
export function mapApiSummaryToTrackerSummary(data){
  return {
    totalMilestoneCount: Number(data.totalMilestoneCount || data.milestones?.length || 0),
    totalInvoiceCount: Number(
      data.totalInvoiceCount ||
      data.totalInvoices ||
      data.totalInvoiceRecords ||
      data.invoices?.length ||
      0,
    ),
    totalMilestoneAmount: toAmount(data.totalMilestoneAmount),
    submittedCount: Number(data.submittedCount || 0),
    submittedAmount: toAmount(data.submittedAmount),
    approvedCount: Number(data.approvedCount || 0),
    approvedAmount: toAmount(data.approvedAmount),
    pendingCount: Number(data.pendingCount || 0),
    pendingInvoiceCount: Number(data.pendingInvoiceCount || 0),
    pendingInvoiceAmount: safeCalc([data.pendingInvoiceAmount], Math.abs),
    invoiceMismatch: safeCalc([data.invoiceMismatch], Math.abs),
    mismatchCount: Number(data.pendingCount || 0),
    mismatchAmount: safeCalc([data.invoiceMismatch], Math.abs),
  };
}

/** Builds a stable key for the row-level details popover shown in milestone/invoice tables. */
export function getRowPopoverKey(
  row,
  index,
) {
  return `${row.invoiceId || "invoice"}|${row.projectId || "project"}|${row.revenueMonth || "month"}|${index}`;
}

export function isInvoiceUploadApiResponse(value) {
  if (!value || typeof value !== "object") {
    return false;
  }

  const response = value;
  return typeof response.message === "string";
}

/** Flattens the failed-invoice and row-error lists from an upload response into a single displayable list. */
export function getInvoiceUploadFailedDetails(
  response,
){
  if (!response) return [];

  const failedInvoiceDetails = (response.failedInvoices || []).map((item) => ({
    key: `failed-${(item.rowNumbers || []).join("-")}-${item.projectId || "NA"}`,
    title: `Row(s) ${(item.rowNumbers || []).join(", ") || "-"} | Project ID: ${item.projectId || "N/A"}`,
    description:
      typeof (item ).errorMessage === "string" &&
      String((item).errorMessage).trim().length > 0
        ? String((item).errorMessage)
        : item.missingFields && item.missingFields.length > 0
          ? `Missing fields: ${item.missingFields.join(", ")}`
          : "Unknown error",
  }));

  const rowErrorDetails = (response.rowErrors || []).map((item) => ({
    key: `row-${item.rowNumber ?? "NA"}-${item.projectId || "NA"}`,
    title: `Row ${item.rowNumber ?? "-"} | Project ID: ${item.projectId || "N/A"}`,
    description: item.errors && item.errors.length > 0 ? item.errors.join(" ") : "Unknown error",
  }));

  return [...failedInvoiceDetails, ...rowErrorDetails];
}


